<script setup>
import { ref, computed } from 'vue';
import Draggable from 'vuedraggable';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useKanbanStore } from 'dashboard/stores/kanban';
import AttributeAPI from 'dashboard/api/attributes';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';

const { t } = useI18n();
const kanban = useKanbanStore();

const dialogRef = ref(null);
const fields = ref([]);
const isBusy = ref(false);

// Mirrors CustomAttributeDefinition's attribute_display_type enum. `list` is the only one
// that needs extra input (its options), so it drives the conditional below.
const TYPES = [
  'text',
  'number',
  'currency',
  'percent',
  'link',
  'date',
  'list',
  'checkbox',
];

const newField = ref({ label: '', type: 'text', values: '' });

const open = async () => {
  dialogRef.value?.open();
  isBusy.value = true;
  try {
    fields.value = [...(await kanban.fetchFieldLayout())];
  } finally {
    isBusy.value = false;
  }
};

const close = () => dialogRef.value?.close();

const persistOrder = async () => {
  try {
    await kanban.saveFieldLayout(fields.value.map(field => field.key));
  } catch (error) {
    useAlert(t('KANBAN.FIELDS.SAVE_FAILED'));
  }
};

// Chatwoot derives nothing from the display name, so build the key here. Accent-stripped
// and snake_cased because attribute_key only accepts letters, numbers, _, . and -.
const keyFrom = label =>
  label
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_|_$/g, '');

const canAdd = computed(() => Boolean(keyFrom(newField.value.label)));

const onAddField = async () => {
  const key = keyFrom(newField.value.label);
  if (!key) return;

  isBusy.value = true;
  try {
    await AttributeAPI.create({
      custom_attribute_definition: {
        attribute_display_name: newField.value.label.trim(),
        attribute_key: key,
        attribute_display_type: newField.value.type,
        attribute_model: 'contact_attribute',
        attribute_values:
          newField.value.type === 'list'
            ? newField.value.values
                .split(',')
                .map(v => v.trim())
                .filter(Boolean)
            : [],
      },
    });
    fields.value = [...(await kanban.fetchFieldLayout())];
    newField.value = { label: '', type: 'text', values: '' };
    useAlert(t('KANBAN.FIELDS.ADDED'));
  } catch (error) {
    useAlert(error?.response?.data?.message || t('KANBAN.FIELDS.ADD_FAILED'));
  } finally {
    isBusy.value = false;
  }
};

// Deleting the definition also removes it from every contact's attributes, which is why
// only custom fields can go — the native columns back the contact list and the card.
const onRemoveField = async field => {
  isBusy.value = true;
  try {
    const definitions = await AttributeAPI.getAttributesByModel();
    const match = (definitions.data || []).find(
      d =>
        d.attribute_key === field.key &&
        d.attribute_model === 'contact_attribute'
    );
    if (match) await AttributeAPI.delete(match.id);
    fields.value = [...(await kanban.fetchFieldLayout())];
    useAlert(t('KANBAN.FIELDS.REMOVED'));
  } catch (error) {
    useAlert(t('KANBAN.FIELDS.REMOVE_FAILED'));
  } finally {
    isBusy.value = false;
  }
};

defineExpose({ open, close });
</script>

<template>
  <Dialog
    ref="dialogRef"
    :title="$t('KANBAN.FIELDS.TITLE')"
    :show-confirm-button="false"
    :cancel-button-label="$t('KANBAN.FIELDS.DONE')"
    width="lg"
  >
    <div class="flex flex-col gap-4">
      <p class="text-xs text-n-slate-11">
        {{ $t('KANBAN.FIELDS.DESCRIPTION') }}
      </p>

      <Draggable
        v-model="fields"
        item-key="key"
        class="flex flex-col gap-1"
        handle=".kanban-field-handle"
        ghost-class="opacity-40"
        @end="persistOrder"
      >
        <template #item="{ element }">
          <div
            class="flex items-center gap-2 px-2 py-2 rounded-lg bg-n-alpha-1"
          >
            <span
              class="kanban-field-handle flex items-center justify-center rounded cursor-grab active:cursor-grabbing size-6 text-n-slate-11 hover:bg-n-alpha-2"
            >
              <Icon icon="i-lucide-grip-vertical" class="size-4" />
            </span>
            <span class="flex-1 text-sm truncate text-n-slate-12">
              {{
                element.native
                  ? $t(`KANBAN.DRAWER.FIELDS.${element.key}`)
                  : element.label
              }}
            </span>
            <span
              class="px-1.5 py-0.5 text-xs rounded bg-n-alpha-2 text-n-slate-11"
            >
              {{ $t(`KANBAN.FIELDS.TYPES.${element.type}`) }}
            </span>
            <span
              v-if="element.native"
              class="px-1.5 py-0.5 text-xs rounded text-n-slate-10"
            >
              {{ $t('KANBAN.FIELDS.NATIVE') }}
            </span>
            <button
              v-else
              :disabled="isBusy"
              class="flex items-center justify-center rounded size-6 text-n-ruby-11 hover:bg-n-ruby-3 disabled:opacity-50"
              :title="$t('KANBAN.FIELDS.REMOVE')"
              :aria-label="$t('KANBAN.FIELDS.REMOVE')"
              @click="onRemoveField(element)"
            >
              <Icon icon="i-lucide-trash-2" class="size-3.5" />
            </button>
          </div>
        </template>
      </Draggable>

      <fieldset class="flex flex-col gap-3 p-3 border rounded-lg border-n-weak">
        <legend class="px-1 text-xs font-medium text-n-slate-11">
          {{ $t('KANBAN.FIELDS.ADD_SECTION') }}
        </legend>

        <div class="flex gap-2">
          <label class="flex flex-col flex-1 gap-1 text-xs text-n-slate-11">
            {{ $t('KANBAN.FIELDS.LABEL') }}
            <input
              v-model="newField.label"
              type="text"
              :placeholder="$t('KANBAN.FIELDS.LABEL_PLACEHOLDER')"
              class="w-full h-9 px-2.5 text-sm rounded-lg reset-base bg-n-alpha-black2 text-n-slate-12 outline outline-1 outline-n-weak focus:outline-n-brand"
            />
          </label>
          <label class="flex flex-col gap-1 text-xs text-n-slate-11">
            {{ $t('KANBAN.FIELDS.TYPE') }}
            <select
              v-model="newField.type"
              class="h-9 px-2 text-sm rounded-lg reset-base bg-n-alpha-black2 text-n-slate-12 outline outline-1 outline-n-weak"
            >
              <option v-for="type in TYPES" :key="type" :value="type">
                {{ $t(`KANBAN.FIELDS.TYPES.${type}`) }}
              </option>
            </select>
          </label>
        </div>

        <label
          v-if="newField.type === 'list'"
          class="flex flex-col gap-1 text-xs text-n-slate-11"
        >
          {{ $t('KANBAN.FIELDS.VALUES') }}
          <input
            v-model="newField.values"
            type="text"
            :placeholder="$t('KANBAN.FIELDS.VALUES_PLACEHOLDER')"
            class="w-full h-9 px-2.5 text-sm rounded-lg reset-base bg-n-alpha-black2 text-n-slate-12 outline outline-1 outline-n-weak"
          />
        </label>

        <button
          :disabled="!canAdd || isBusy"
          class="h-9 text-sm font-medium rounded-lg bg-n-brand text-white hover:opacity-90 disabled:opacity-50"
          @click="onAddField"
        >
          {{ $t('KANBAN.FIELDS.ADD') }}
        </button>
      </fieldset>
    </div>
  </Dialog>
</template>
